import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { Send01 } from "@velaro/velaro-component-lib";

// ── Types ─────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface TicketForm {
  subject: string;
  description: string;
  contactName: string;
  contactEmail: string;
}

interface TicketResult {
  ticketNumber: string;
  id: number;
}

interface NearLimit {
  feature: string;
  current: number;
  max: number;
  percentUsed: number;
}

interface SubscriptionStatus {
  enabledFeatures: string[];
  disabledFeatures: Array<{ flag: string; name: string; description: string; package: string; learnMorePath: string }>;
  nearLimits: NearLimit[];
  usage: Record<string, number | string>;
  summary: string;
}

interface Props {
  /** Current page context sent to the AI to tailor responses (e.g. "workflows", "integrations/bigcommerce") */
  context?: string;
  /** Called when the user closes the drawer */
  onClose: () => void;
}

type Product = "v20" | "v10";

const PRODUCT_LABELS: Record<Product, string> = {
  v20: "Velaro Admin (v20)",
  v10: "Velaro Console (v10)"
};

// ── Frustration detection ─────────────────────────────────────────────────
// Client-side keyword matching. Intentionally broad — false positives just
// show a polite nudge, not an error. Real sentiment API can replace this later.

const FRUSTRATION_PHRASES = [
  "frustrated",
  "frustrating",
  "doesn't work",
  "doesn't help",
  "not working",
  "not helpful",
  "wrong answer",
  "incorrect",
  "useless",
  "terrible",
  "horrible",
  "hate this",
  "still stuck",
  "still doesn't",
  "still not",
  "keeps failing",
  "can't figure",
  "can't do",
  "can't get",
  "cannot do",
  "cannot get",
  "giving up",
  "this is broken",
  "this is bad",
  "waste of time",
  "no help",
  "not helping",
  "didn't answer",
  "didn't help",
  "doesn't answer",
  "wrong",
  "confused",
  "confusing",
];

function detectFrustration(text: string): boolean {
  const lower = text.toLowerCase();
  return FRUSTRATION_PHRASES.some((p) => lower.includes(p));
}

// ── Quick-start suggestion chips ─────────────────────────────────────────

const SUGGESTIONS = [
  "How do I set up a workflow?",
  "How does AI automation work?",
  "How do I connect BigCommerce?",
  "How do I create a support ticket workflow?",
  "What channels does Velaro support?",
  "How do I use variables in workflows?",
];

// ── Markdown → simple HTML (lightweight, no dependency) ──────────────────

function renderMarkdown(text: string): string {
  // Protect code blocks first (extract before escaping)
  const codeBlocks: string[] = [];
  const inlineCodes: string[] = [];
  let s = text
    .replace(/```([\s\S]*?)```/g, (_, inner) => {
      const code = inner.replace(/^\w*\n/, "");
      codeBlocks.push(`<pre class="bg-slate-900 text-green-300 rounded-lg p-3 text-xs my-2 overflow-x-auto whitespace-pre-wrap border border-slate-700"><code>${inner.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</code></pre>`);
      return `\x00CODE${codeBlocks.length - 1}\x00`;
    })
    .replace(/`([^`]+)`/g, (_, inner) => {
      inlineCodes.push(`<code class="bg-slate-700 text-green-300 rounded px-1 text-xs">${inner.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</code>`);
      return `\x00IC${inlineCodes.length - 1}\x00`;
    });

  // Escape HTML in the rest
  s = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  s = s
    // Navigation links [→ label](/path)
    .replace(/\[→\s*([^\]]+)\]\(([^)]+)\)/g,
      '<a class="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 underline cursor-pointer font-medium" data-nav="$2">→ $1</a>')
    // Regular markdown links [label](url)
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a class="text-blue-400 hover:text-blue-300 underline" href="$2" target="_blank" rel="noreferrer">$1</a>')
    // H3 headings (### text)
    .replace(/^###\s+(.+)$/gm, '<p class="text-white font-semibold text-sm mt-3 mb-1">$1</p>')
    // H2 headings (## text)
    .replace(/^##\s+(.+)$/gm, '<p class="text-white font-bold text-sm mt-3 mb-1">$1</p>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>')
    // Numbered lists
    .replace(/^(\d+)\.\s+(.+)$/gm, '<div class="flex gap-2 my-0.5"><span class="text-blue-400 font-semibold min-w-[18px]">$1.</span><span>$2</span></div>')
    // Bullet lists
    .replace(/^[-*]\s+(.+)$/gm, '<div class="flex gap-2 my-0.5"><span class="text-slate-400">•</span><span>$1</span></div>')
    // Paragraphs
    .replace(/\n\n/g, '<div class="h-2"></div>')
    .replace(/\n/g, "<br/>");

  // Restore code blocks
  s = s.replace(/\x00CODE(\d+)\x00/g, (_, i) => codeBlocks[+i]);
  s = s.replace(/\x00IC(\d+)\x00/g, (_, i) => inlineCodes[+i]);
  return s;
}

// ── Main component ────────────────────────────────────────────────────────

export default function CopilotAssistant({ context = "", onClose }: Props) {
  const api = useApi();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Minimize / maximize
  const [minimized, setMinimized] = useState(false);

  // Drag state — offset from default bottom-right position, persisted across open/close
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>(() => {
    try {
      const saved = sessionStorage.getItem("copilot-pos");
      return saved ? JSON.parse(saved) : { x: 0, y: 0 };
    } catch {
      return { x: 0, y: 0 };
    }
  });
  const dragStart = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);

  const onHeaderMouseDown = (e: React.MouseEvent) => {
    // Only drag on left-click, not on buttons inside header
    if ((e.target as HTMLElement).closest("button")) return;
    dragStart.current = { mx: e.clientX, my: e.clientY, ox: dragOffset.x, oy: dragOffset.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const next = {
        x: dragStart.current.ox + (e.clientX - dragStart.current.mx),
        y: dragStart.current.oy + (e.clientY - dragStart.current.my),
      };
      setDragOffset(next);
      try { sessionStorage.setItem("copilot-pos", JSON.stringify(next)); } catch { /* ignore */ }
    };
    const onUp = () => { dragStart.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: "assistant",
      content:
        "Hi! I'm **Velaro AI**. I can help you set up workflows, connect integrations, troubleshoot issues, and answer questions about the platform.\n\nWhat would you like to do today?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Frustration detection
  const [frustrationCount, setFrustrationCount] = useState(0);
  const [showFrustrationBanner, setShowFrustrationBanner] = useState(false);
  const [feedbackDismissed, setFeedbackDismissed] = useState(false);

  // Feedback form state (lightweight — no ticket, just "what couldn't you do?")
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // Subscription status (fetched once on open — used for near-limit banners)
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [nearLimitDismissed, setNearLimitDismissed] = useState(false);
  const [sendingUpgrade, setSendingUpgrade] = useState<string | null>(null);
  const [upgradeSent, setUpgradeSent] = useState<string | null>(null);

  // Ticket creation state
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState<TicketForm>({
    subject: "",
    description: "",
    contactName: "",
    contactEmail: "",
  });
  const [ticketResult, setTicketResult] = useState<TicketResult | null>(null);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [product, setProduct] = useState<Product>("v20");

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Fetch subscription status once on open (silent — used for near-limit banners and upgrade prompts)
  useEffect(() => {
    let cancelled = false;
    api.admin.get("Copilot/SubscriptionStatus")
      .then((res) => res.json())
      .then((data: SubscriptionStatus) => {
        if (!cancelled) setSubStatus(data);
      })
      .catch(() => { /* non-critical — just won't show banners */ });
    return () => { cancelled = true; };
  }, [api]);

  const buildSessionSummary = useCallback(
    () =>
      messages
        .slice(-10)
        .map((m) => `${m.role === "user" ? "User" : "Copilot"}: ${m.content}`)
        .join("\n\n"),
    [messages]
  );

  const sendMessage = useCallback(
    async (userText: string) => {
      const text = userText.trim();
      if (!text || loading) return;

      setInput("");
      setError(null);

      const newMessages: ChatMessage[] = [
        ...messages,
        { role: "user", content: text },
      ];
      setMessages(newMessages);
      setLoading(true);

      // Client-side frustration detection
      if (detectFrustration(text)) {
        setFrustrationCount((c) => {
          const next = c + 1;
          if (next >= 1 && !feedbackDismissed) setShowFrustrationBanner(true);
          return next;
        });

        // Fire-and-forget background report to server (no modal, silent)
        try {
          const summary = newMessages
            .slice(-10)
            .map((m) => `${m.role === "user" ? "User" : "Copilot"}: ${m.content}`)
            .join("\n\n");
          api.admin.post("Copilot/Feedback", {
            feedbackText: text,
            sessionSummary: summary,
            triggerSource: "frustration_detected",
            pageContext: context,
          }).catch(() => {/* silent */});
        } catch {/* silent */}
      }

      try {
        const res = await api.admin.post("Copilot/Chat", {
          messages: newMessages,
          context,
        });
        const data = await res.json();

        if (data.success === false) {
          setError(data.error ?? "Something went wrong.");
          setMessages([
            ...newMessages,
            {
              role: "assistant",
              content:
                data.reply ??
                "I'm having trouble right now. Please try again or create a support ticket.",
            },
          ]);
        } else {
          setMessages([
            ...newMessages,
            { role: "assistant", content: data.reply ?? "" },
          ]);
        }
      } catch {
        setError("Connection error. Please check your network and try again.");
        setMessages([
          ...newMessages,
          {
            role: "assistant",
            content:
              "I'm having trouble connecting. You can also reach support at **support@velaro.com**.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [api, messages, context, loading, feedbackDismissed]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
    // Shift+Enter inserts a newline (default textarea behavior — let it through)
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) return;
    setSubmittingFeedback(true);
    try {
      await api.admin.post("Copilot/Feedback", {
        feedbackText: feedbackText.trim(),
        sessionSummary: buildSessionSummary(),
        triggerSource: "user_submitted",
        pageContext: context,
      });
      setFeedbackSent(true);
      setFeedbackText("");
      setShowFeedbackForm(false);
      setShowFrustrationBanner(false);
      setFeedbackDismissed(true);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Thanks for the feedback — our team will review it and improve the Copilot. Is there anything else I can help with?",
        },
      ]);
    } catch {
      setError("Could not send feedback. Please try again.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketForm.subject.trim()) return;
    setSubmittingTicket(true);
    try {
      const res = await api.admin.post("Copilot/Ticket", {
        ...ticketForm,
        sessionSummary: buildSessionSummary(),
      });
      const data = await res.json();
      setTicketResult({ ticketNumber: data.ticketNumber, id: data.id });
      setShowTicketForm(false);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Your support ticket has been created: **${data.ticketNumber}**\n\nWe'll reach out to **${ticketForm.contactEmail || "you"}** within 1 business day. Is there anything else I can help you with?`,
        },
      ]);
    } catch {
      setError("Could not create ticket. Please email support@velaro.com.");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleRequestAccess = async (featureName: string) => {
    setSendingUpgrade(featureName);
    try {
      await api.admin.post("Copilot/UpgradeRequest", { feature: featureName, notes: buildSessionSummary() });
      setUpgradeSent(featureName);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Your request to enable **${featureName}** has been sent to the Velaro team. We'll follow up within 1 business day. In the meantime, is there anything else I can help you with?`,
        },
      ]);
    } catch {
      setError("Could not send request. Please email sales@velaro.com.");
    } finally {
      setSendingUpgrade(null);
    }
  };

  const activeView = showFeedbackForm ? "feedback" : showTicketForm ? "ticket" : "chat";

  return (
    <div
      ref={panelRef}
      style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
      className={`fixed bottom-20 right-5 z-50 flex flex-col w-[400px] max-w-[calc(100vw-24px)] bg-slate-900 border border-slate-700 shadow-2xl rounded-2xl overflow-hidden transition-[height] duration-150 ${minimized ? "h-auto" : "h-[580px]"}`}
    >
      {/* Header — drag handle */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700 flex-shrink-0 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={onHeaderMouseDown}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
            {/* Velaro V mark — white */}
            <svg width="16" height="14" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z" fill="white"/>
              <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
              <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
            </svg>
          </div>
          <div>
            <div className="text-white font-semibold text-sm">Velaro AI</div>
            <div className="text-slate-400 text-xs">AI-powered help</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {activeView === "chat" && !ticketResult && !minimized && (
            <>
              <button
                onClick={() => setShowFeedbackForm(true)}
                className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors"
                title="Send feedback"
              >
                Feedback
              </button>
              <button
                onClick={() => setShowTicketForm(true)}
                className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-700 transition-colors"
                title="Create a support ticket"
              >
                Ticket
              </button>
            </>
          )}
          {/* Minimize / restore */}
          <button
            onClick={() => setMinimized((m) => !m)}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors"
            aria-label={minimized ? "Restore" : "Minimize"}
          >
            {minimized ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {!minimized && <>

      {/* Feedback form */}
      {activeView === "feedback" && (
        <div className="flex-1 overflow-y-auto p-4 bg-slate-900">
          <h3 className="text-white font-semibold mb-2">Send Feedback</h3>
          <p className="text-slate-400 text-sm mb-4">
            Tell us what you couldn't do, or where the Copilot got it wrong.
            Your feedback goes directly to our product team.
          </p>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={6}
            placeholder="What were you trying to do? What went wrong or wasn't helpful?"
            className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
          />
          {error && (
            <div className="mt-3 text-red-400 text-sm bg-red-900/30 border border-red-700 rounded p-2">
              {error}
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || !feedbackText.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-4 py-2 text-sm font-medium transition-colors"
            >
              {submittingFeedback ? "Sending..." : "Send Feedback"}
            </button>
            <button
              onClick={() => { setShowFeedbackForm(false); setFeedbackDismissed(true); setShowFrustrationBanner(false); }}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
          <p className="text-slate-500 text-xs mt-3">
            No email required — we'll use your account info. For urgent issues, use "Create Ticket" instead.
          </p>
        </div>
      )}

      {/* Ticket creation form */}
      {activeView === "ticket" && (
        <div className="flex-1 overflow-y-auto p-4 bg-slate-900">
          <h3 className="text-white font-semibold mb-4">Create Support Ticket</h3>
          <p className="text-slate-400 text-sm mb-4">
            Our team will respond within 1 business day. Your Copilot session
            will be attached as context.
          </p>
          <div className="space-y-3">
            <div>
              <label className="text-slate-300 text-xs mb-1 block">Subject *</label>
              <input
                type="text"
                value={ticketForm.subject}
                onChange={(e) => setTicketForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="What do you need help with?"
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="text-slate-300 text-xs mb-1 block">Description</label>
              <textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm((f) => ({ ...f, description: e.target.value }))}
                rows={4}
                placeholder="Describe the issue in detail..."
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
            <div>
              <label className="text-slate-300 text-xs mb-1 block">Your Name</label>
              <input
                type="text"
                value={ticketForm.contactName}
                onChange={(e) => setTicketForm((f) => ({ ...f, contactName: e.target.value }))}
                placeholder="Full name"
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="text-slate-300 text-xs mb-1 block">Email for reply</label>
              <input
                type="email"
                value={ticketForm.contactEmail}
                onChange={(e) => setTicketForm((f) => ({ ...f, contactEmail: e.target.value }))}
                placeholder="your@email.com"
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          {error && (
            <div className="mt-3 text-red-400 text-sm bg-red-900/30 border border-red-700 rounded p-2">
              {error}
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSubmitTicket}
              disabled={submittingTicket || !ticketForm.subject.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-4 py-2 text-sm font-medium transition-colors"
            >
              {submittingTicket ? "Submitting..." : "Submit Ticket"}
            </button>
            <button
              onClick={() => setShowTicketForm(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Chat messages */}
      {activeView === "chat" && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
            {/* Suggestion chips + upgrade prompts — only shown before user sends first message */}
            {messages.length === 1 && (
              <>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-600 rounded-full px-3 py-1.5 transition-colors text-left"
                    >
                      {s}
                    </button>
                  ))}
                  {subStatus && subStatus.disabledFeatures.length > 0 && (
                    <button
                      onClick={() => sendMessage("What features can I unlock for my account?")}
                      className="text-xs bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 hover:text-blue-200 border border-blue-700/50 rounded-full px-3 py-1.5 transition-colors text-left"
                    >
                      ✦ Upgrade to unlock {subStatus.disabledFeatures.length} more features
                    </button>
                  )}
                </div>

                {/* Near-limit warning shown at the top of the chat */}
                {subStatus && subStatus.nearLimits.length > 0 && !nearLimitDismissed && (
                  <div className="mt-3 bg-amber-900/25 border border-amber-700/50 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-amber-300 text-xs font-semibold mb-1">Approaching usage limits</p>
                        {subStatus.nearLimits.map((l) => (
                          <div key={l.feature} className="flex items-center gap-2 mb-1">
                            <div className="flex-1">
                              <div className="flex justify-between text-xs text-amber-400/80 mb-0.5">
                                <span>{l.feature}</span>
                                <span>{l.current} / {l.max}</span>
                              </div>
                              <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${l.percentUsed >= 90 ? "bg-red-500" : "bg-amber-500"}`}
                                  style={{ width: `${Math.min(l.percentUsed, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <p className="text-amber-500/70 text-xs mt-1">Ask me about upgrading your limits.</p>
                      </div>
                      <button
                        onClick={() => setNearLimitDismissed(true)}
                        className="text-amber-600 hover:text-amber-400 text-xs mt-0.5 flex-shrink-0"
                        aria-label="Dismiss"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-6 h-6 rounded-lg bg-slate-700 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                    <svg width="12" height="10" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z" fill="white"/>
                      <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
                      <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
                    </svg>
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : "bg-slate-800 text-slate-200 rounded-bl-sm"
                  }`}
                >
                  {msg.role === "assistant" ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                      onClick={(e) => {
                        const a = (e.target as HTMLElement).closest("[data-nav]");
                        if (a) { e.preventDefault(); navigate(a.getAttribute("data-nav") ?? "/"); }
                      }}
                    />
                  ) : (
                    <span>{msg.content}</span>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="w-6 h-6 rounded-lg bg-slate-700 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                  <svg width="12" height="10" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
                    <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
                  </svg>
                </div>
                <div className="bg-slate-800 text-slate-400 rounded-2xl rounded-bl-sm px-4 py-3 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="animate-bounce" style={{ animationDelay: "0ms" }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: "150ms" }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: "300ms" }}>•</span>
                  </span>
                </div>
              </div>
            )}

            {/* Ticket result banner */}
            {ticketResult && (
              <div className="bg-green-900/40 border border-green-700 rounded-lg p-3 text-sm">
                <div className="text-green-400 font-semibold mb-1">
                  Ticket Created: {ticketResult.ticketNumber}
                </div>
                <div className="text-slate-300">We'll follow up within 1 business day.</div>
              </div>
            )}

            {/* Feedback sent confirmation */}
            {feedbackSent && (
              <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3 text-sm text-blue-300">
                Feedback received — thank you for helping us improve!
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Frustration detected banner */}
          {showFrustrationBanner && !feedbackDismissed && (
            <div className="px-4 py-3 bg-amber-900/30 border-t border-amber-700 flex items-start gap-3 flex-shrink-0">
              <span className="text-amber-400 text-lg leading-none mt-0.5">!</span>
              <div className="flex-1">
                <p className="text-amber-300 text-xs font-semibold">Not getting what you need?</p>
                <p className="text-amber-400/80 text-xs mt-0.5">Tell us what's missing and we'll improve the Copilot.</p>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => setShowFeedbackForm(true)}
                  className="text-xs bg-amber-600 hover:bg-amber-700 text-white rounded px-2 py-1 transition-colors whitespace-nowrap"
                >
                  Send Feedback
                </button>
                <button
                  onClick={() => { setShowFrustrationBanner(false); setFeedbackDismissed(true); }}
                  className="text-xs text-amber-500 hover:text-amber-300 text-center transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="px-4 py-2 bg-red-900/30 border-t border-red-800 text-red-400 text-xs flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-white">
                ×
              </button>
            </div>
          )}

          {/* Input area */}
          <div className="px-4 py-3 bg-slate-800 border-t border-slate-700 flex-shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about Velaro..."
                rows={1}
                disabled={loading}
                className="flex-1 bg-slate-700 border border-slate-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none leading-5 max-h-32 overflow-y-auto"
                style={{ minHeight: "42px" }}
                onInput={(e) => {
                  const el = e.currentTarget;
                  el.style.height = "auto";
                  el.style.height = Math.min(el.scrollHeight, 128) + "px";
                }}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                className="w-[42px] h-[42px] bg-cornflower-blue-600 hover:bg-cornflower-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Send message"
              >
                <Send01 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </>
      )}

      </>}
    </div>
  );
}
